import {
  GatewayAssujetti,
  GatewayControleQuinquennal,
} from "@conformite/gateway";
import { userSession } from "@src/data/session/UserSession";

/**
 * Query de `GET :id/analyse/estimation` (conception EN-1476
 * §3.5). `AnalyseRunScope` (form-api) n'est pas réexporté par le barrel
 * gateway (seuls les `*.definition.ts` de controller le sont) : les valeurs
 * littérales ci-dessous doivent rester synchronisées avec
 * `FormApiControleQuinquennal.AnalyseRunScope`.
 */
export type AnalyseEstimationScopeParams =
  | { scope: "DOSSIER" }
  | { scope: "AXE"; idAxe: string }
  | { scope: "SOUS_AXE"; idAxe: string; subAxis: string };

function buildCQListQuery(
  params: GatewayControleQuinquennal.ControleQuinquennalAdminSearchRequestType
): string {
  const search = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  if (params.search) search.set("search", params.search);
  if (params.sortField) search.set("sortField", params.sortField);
  if (params.sortOrder) search.set("sortOrder", params.sortOrder);
  if (params.pipelineStages?.length) {
    search.set("pipelineStages", JSON.stringify(params.pipelineStages));
  }
  if (params.compteursPipelineStages?.length) {
    search.set(
      "compteursPipelineStages",
      JSON.stringify(params.compteursPipelineStages)
    );
  }
  if (params.millesimes?.length) {
    search.set("millesimes", JSON.stringify(params.millesimes));
  }
  if (params.verificateurId)
    search.set("verificateurId", params.verificateurId);
  if (params.mesControles) search.set("mesControles", "true");
  if (params.dateDebutFrom) search.set("dateDebutFrom", params.dateDebutFrom);
  if (params.dateDebutTo) search.set("dateDebutTo", params.dateDebutTo);
  if (params.dateDepotFrom) search.set("dateDepotFrom", params.dateDepotFrom);
  if (params.dateDepotTo) search.set("dateDepotTo", params.dateDepotTo);
  if (params.dateFinFrom) search.set("dateFinFrom", params.dateFinFrom);
  if (params.dateFinTo) search.set("dateFinTo", params.dateFinTo);
  return search.toString();
}

export class ControleQuinquennalApi {
  static async list(
    params: GatewayControleQuinquennal.ControleQuinquennalAdminSearchRequestType
  ) {
    const query = buildCQListQuery(params);
    const { data } = await userSession.axiosInstance.get<{
      data: GatewayControleQuinquennal.ControleQuinquennalAdminListResponse[];
      total: number;
      totalEnCours: number;
      compteurs?: GatewayControleQuinquennal.ControleQuinquennalAdminCompteurs;
      availableMillesimes: number[];
    }>(`/admin/controle-quinquennal?${query}`);

    return data;
  }

  static async listAll(
    params: Omit<
      GatewayControleQuinquennal.ControleQuinquennalAdminSearchRequestType,
      "page" | "limit"
    >,
    pageSize = 200
  ): Promise<
    GatewayControleQuinquennal.ControleQuinquennalAdminListResponse[]
  > {
    const first = await this.list({ ...params, page: 1, limit: pageSize });
    const pageCount = Math.ceil(first.total / pageSize);

    if (pageCount <= 1) return first.data;

    const remaining = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) =>
        this.list({ ...params, page: i + 2, limit: pageSize })
      )
    );

    return [first.data, ...remaining.map(({ data }) => data)].flat();
  }

  /**
   * Métriques d'un lot de contrôles : hors de la liste, car leur calcul passe
   * par HubSpot puis par tous les questionnaires du contrôle et de ses
   * assujettis. Le front les demande par paquets et les affiche au fil de l'eau.
   */
  static async metriques(
    controles: { id: string; idEntreprise: string; millesime: number }[]
  ): Promise<
    Record<
      string,
      GatewayControleQuinquennal.ControleQuinquennalMetriquesAvecSiren
    >
  > {
    if (controles.length === 0) return {};
    const { data } = await userSession.axiosInstance.post<
      Record<
        string,
        GatewayControleQuinquennal.ControleQuinquennalMetriquesAvecSiren
      >
    >("/admin/controle-quinquennal/metriques", { controles });

    return data;
  }

  private static async exportPage(
    params: GatewayControleQuinquennal.ControleQuinquennalAdminSearchRequestType
  ) {
    const query = buildCQListQuery(params);
    const { data } = await userSession.axiosInstance.get<{
      data: GatewayControleQuinquennal.ControleQuinquennalAdminExportResponse[];
      total: number;
      totalEnCours: number;
    }>(`/admin/controle-quinquennal/export?${query}`);

    return data;
  }

  static async exportAll(
    params: Omit<
      GatewayControleQuinquennal.ControleQuinquennalAdminSearchRequestType,
      "page" | "limit"
    >
  ): Promise<
    GatewayControleQuinquennal.ControleQuinquennalAdminExportResponse[]
  > {
    const pageSize = 1000;
    const first = await this.exportPage({
      ...params,
      page: 1,
      limit: pageSize,
    });
    const pageCount = Math.ceil(first.total / pageSize);

    if (pageCount <= 1) return first.data;

    const remaining = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) =>
        this.exportPage({ ...params, page: i + 2, limit: pageSize })
      )
    );

    return [...first.data, ...remaining.flatMap((r) => r.data)];
  }

  static async getCQWithAxes(entrepriseId: string, idCQ?: string) {
    const query = idCQ
      ? `?${new URLSearchParams({ idControleQuinquennal: idCQ }).toString()}`
      : "";
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAdminResponse>(
        `/admin/controle-quinquennal/${entrepriseId}${query}`
      );
    return data;
  }

  static async getCQConversation(
    idControleQuinquennal: string,
    idAxe: string,
    axeName: string
  ) {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.RemediationConversationResponse>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/conversations/${axeName}`
      );
    return data.conversations;
  }

  static async validate(idCQ: string, siren: string, file?: File) {
    const formData = new FormData();
    if (file) formData.append("document", file);
    return userSession.axiosInstance.put(
      `/admin/controle-quinquennal/${idCQ}/validate/${siren}`,
      formData
    );
  }

  static async getRapportPreview(idCQ: string): Promise<Blob> {
    const { data } = await userSession.axiosInstance.get<Blob>(
      `/admin/controle-quinquennal/${idCQ}/rapport-preview`,
      { responseType: "blob" }
    );
    return data;
  }

  static async getRapportPreviewDocx(idCQ: string): Promise<Blob> {
    const { data } = await userSession.axiosInstance.get<Blob>(
      `/admin/controle-quinquennal/${idCQ}/rapport-preview-docx`,
      { responseType: "blob" }
    );
    return data;
  }

  static async createRemediation(
    idControleQuinquennal: string,
    idAxe: string,
    payload: GatewayControleQuinquennal.ControleQuinquenalRemediationRequestType,
    files: File[]
  ) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("documents", file);
    });
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(
        key,
        key === "idFiles" ? JSON.stringify(value) : String(value)
      );
    });
    const { data } =
      await userSession.axiosInstance.post<GatewayControleQuinquennal.ControleQuinquenalRemediationResponseType>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/remediation`,
        formData
      );
    return data;
  }

  static async updateRemediation(
    idControleQuinquennal: string,
    idAxe: string,
    idRemediation: string,
    payload: GatewayControleQuinquennal.ControleQuinquenalRemediationRequestType,
    files: File[]
  ) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("documents", file);
    });
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(
        key,
        key === "idFiles" ? JSON.stringify(value) : String(value)
      );
    });
    const { data } =
      await userSession.axiosInstance.put<GatewayControleQuinquennal.ControleQuinquenalRemediationResponseType>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/remediation/${idRemediation}`,
        formData
      );
    return data;
  }

  static async getRemediationByAxeId(
    idControleQuinquennal: string,
    idAxe: string
  ) {
    const { data } = await userSession.axiosInstance.get<
      GatewayControleQuinquennal.ControleQuinquennalRemediationWithFileResponseType[]
    >(
      `/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/remediation`
    );
    return data;
  }

  static async validateAxe(idControleQuinquennal: string, idAxe: string) {
    const { data } =
      await userSession.axiosInstance.put<GatewayControleQuinquennal.ControleQuinquennalAxeFile>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/validate`
      );
    return data;
  }

  static async cancelBatch(
    ids: string[]
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalCancelBatchResponse> {
    const { data } =
      await userSession.axiosInstance.post<GatewayControleQuinquennal.ControleQuinquennalCancelBatchResponse>(
        "/admin/controle-quinquennal/cancel-batch",
        { ids }
      );
    return data;
  }

  static async launchAnalysis(
    idControleQuinquennal: string
  ): Promise<{ status: string }> {
    const { data } = await userSession.axiosInstance.post<{ status: string }>(
      `/admin/controle-quinquennal/${idControleQuinquennal}/analyse/launch`
    );
    return data;
  }

  // Recette uniquement (endpoint gardé côté gateway). Relance l'analyse IA d'un axe.
  static async relaunchAxeAnalysis(
    idControleQuinquennal: string,
    idAxe: string
  ): Promise<{ status: string }> {
    const { data } = await userSession.axiosInstance.post<{ status: string }>(
      `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/analyse/relaunch`
    );
    return data;
  }

  static async getCQAxe(idControleQuinquennal: string, idAxe: string) {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAxeWithFilesAndTotal>(
        `/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}`
      );
    return data;
  }

  static async listCQAxeWithFiles(
    idControleQuinquennal: string,
    idAxe: string
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalAxeWithFiles> {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAxeWithFiles>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/files`
      );

    return data;
  }

  static async listCQAxeWithFilesPaginated(
    idControleQuinquennal: string,
    idAxe: string,
    search?: string,
    limit: number = 20
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalAxeWithFilesAvecTypesAndTotal> {
    const params = new URLSearchParams({
      limit: String(limit),
      ...(search && { search }),
    }).toString();

    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAxeWithFilesAvecTypesAndTotal>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/files?${params}`
      );

    return data;
  }

  static async listCQPersonnesAssujetties(
    millesime: number,
    siren: string,
    search?: string,
    limit: number = 10
  ): Promise<GatewayAssujetti.ListAssujettiWithAvancementAndTotalResponseType> {
    const params = new URLSearchParams({
      limit: String(limit),
      ...(search && { search }),
    }).toString();

    const { data } =
      await userSession.axiosInstance.get<GatewayAssujetti.ListAssujettiWithAvancementAndTotalResponseType>(
        `${GatewayAssujetti.listAssujettiUrl}/${millesime}/${siren}/admin?${params}`
      );

    return data;
  }

  // MR-G — Vérifications IA par sous-axe de l'axe assujetti (personne ×
  // dimension + 'LISTE'). Renvoie [] tant que CQ_ASSUJETTI_AI !== 'on' côté
  // gateway : la feature reste invisible (aucun changement d'UI).
  static async listAxeSubAxisVerifications(
    idControleQuinquennal: string,
    idAxe: string
  ): Promise<
    GatewayControleQuinquennal.ControleQuinquennalSubAxisVerification[]
  > {
    const { data } = await userSession.axiosInstance.get<
      GatewayControleQuinquennal.ControleQuinquennalSubAxisVerification[]
    >(
      `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/verifications`
    );
    return data;
  }

  // MR-G — Ré-émission unitaire d'UN sous-axe FAILED/timeout (gardes côté
  // gateway : recette + flag + état de la ligne).
  static async relaunchSubAxisAnalysis(
    idControleQuinquennal: string,
    idAxe: string,
    subAxis: string
  ): Promise<GatewayControleQuinquennal.RelaunchSubAxisAnalysisResponse> {
    const { data } =
      await userSession.axiosInstance.post<GatewayControleQuinquennal.RelaunchSubAxisAnalysisResponse>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/verifications/relaunch`,
        { subAxis }
      );
    return data;
  }

  // Estimation coût/durée d'un lancement (conception EN-1476
  // §3.5), préchargée par le front au montage de la page. Recette uniquement
  // (garde côté service).
  static async getAnalyseEstimation(
    idControleQuinquennal: string,
    params: AnalyseEstimationScopeParams
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalAnalyseEstimationResponse> {
    const search = new URLSearchParams({ scope: params.scope });
    if ("idAxe" in params) search.set("idAxe", params.idAxe);
    if ("subAxis" in params) search.set("subAxis", params.subAxis);
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAnalyseEstimationResponse>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/analyse/estimation?${search.toString()}`
      );
    return data;
  }
}
